import { switchMap } from 'rxjs/operators';
import {
  actionCreator,
  collectRoutines,
  dispatchAction,
  persistentReducedStream,
  reducer,
  routine,
  extractPayload,
  ofType,
  streamReducer,
} from '@ardoq/rxbeach';
import { logError } from '@ardoq/logging';
import { dishonestCurrentUser$ as currentUser$ } from 'streams/currentUser/currentUser$';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';
import { alert } from '@ardoq/modal';
import { getArdoqErrorMessage, isArdoqError } from '@ardoq/common-helpers';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type WithI18nLocale } from '@ardoq/i18n';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { combineLatest, map } from 'rxjs';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { ExtractStreamShape } from 'tabview/types';
import { workspaceFieldAccessibility$ } from 'streams/fields/workspaceFieldAccessibility$';
import { workspaceApi } from '@ardoq/api';
import { APICurrentUser } from '@ardoq/api-types';

export type CopyWsStreamState = {
  organizations: { label: string; name: string }[];
  workspaces: { id: string; name: string }[];
  success: boolean;
  error?: string;
};

export type TargetOrg = {
  label: string;
  value: string;
};

type CopyRequest = {
  selectedWsIds: Set<string>;
  targetOrg: TargetOrg;
};

export const copyWorkspacesToOrg = actionCreator<CopyRequest>(
  '[copy to org] DO_COPY'
);

export const copySuccess = actionCreator('[copy to org] COPY_SUCCESS');

export const copyError = actionCreator<string>('[copy to org] COPY_FAIL');

export const resetCopyWs = actionCreator('[copy to org] RESET');

const handleCopyWorkspaces = routine(
  ofType(copyWorkspacesToOrg),
  extractPayload(),
  switchMap(async ({ selectedWsIds, targetOrg }) => {
    const response = await workspaceApi.copyToOrg(targetOrg.value, [
      ...selectedWsIds,
    ]);
    if (isArdoqError(response)) {
      dispatchAction(copyError(getArdoqErrorMessage(response)));
      logError(response, 'Copy workspaces to organization failed');
      return;
    }
    dispatchAction(copySuccess());
    dispatchAction(resetCopyWs());
    const copiedWorkspacesCount = response.data.workspaces.length;
    // i18nLocale$.state OK here: imperative success alert from routine
    const i18nLocale = i18nLocale$.state;
    alert({
      title: t(
        i18nLocale,
        copiedWorkspacesCount === 1
          ? 'copyWsToOrg:successAlertTitle_one'
          : 'copyWsToOrg:successAlertTitle_other',
        copiedWorkspacesCount === 1 ? 'Workspace copied' : 'Workspaces copied'
      ),
      text: t(
        i18nLocale,
        copiedWorkspacesCount === 1
          ? 'copyWsToOrg:successAlert_one'
          : 'copyWsToOrg:successAlert_other',
        { count: copiedWorkspacesCount, targetOrg: targetOrg.label },
        copiedWorkspacesCount === 1
          ? 'Copied workspace to {{targetOrg}}.'
          : 'Copied {{count}} workspaces to {{targetOrg}}.'
      ),
    });
  })
);

const startState: CopyWsStreamState = {
  organizations: [],
  workspaces: [],
  success: false,
};

const copySuccessReducer = (state: CopyWsStreamState) => ({
  ...state,
  success: true,
});

const resetReducer = (state: CopyWsStreamState): CopyWsStreamState => ({
  ...state,
  success: false,
  error: undefined,
});

const copyErrorReducer = (
  state: CopyWsStreamState,
  error: string
): CopyWsStreamState => ({
  ...state,
  error,
});

const workspacesAndPermissionContext$ = combineLatest([
  workspaces$,
  currentUserPermissionContext$,
  workspaceFieldAccessibility$,
]);
const workspacesReducer = (
  state: CopyWsStreamState,
  [
    workspaces,
    permissionContext,
    workspaceFieldAccessibility,
  ]: ExtractStreamShape<typeof workspacesAndPermissionContext$>
) => ({
  ...state,
  workspaces: Object.values(workspaces.byId)
    .filter(({ _id }) =>
      workspaceAccessControlInterface.canCopyWorkspace(
        permissionContext,
        workspaceFieldAccessibility,
        _id
      )
    )
    .map(({ _id: id, name }) => ({
      id,
      name,
    })),
});

const getCopyableTargetOrgs = (user: APICurrentUser) =>
  // e2e tests for presentations are failing in this place,
  // it's probably when we test presentation user is not loggedIn
  // tests were still passing on old ardoq-front repo due to
  // outdated jest, but on monorepo we are running new jest
  // which is catching the error. We should check if the error
  // is happening on production presntation
  user.organizations?.filter(
    (org: { label: any; role: string }) =>
      org.label !== user.organization?.label &&
      ['admin', 'writer'].includes(org.role)
  ) || [];

export const hasCopyableTargetOrgs = (user: APICurrentUser | null): boolean =>
  Boolean(user && getCopyableTargetOrgs(user).length > 0);

const currentUserReducer = (
  state: CopyWsStreamState,
  user: APICurrentUser
): CopyWsStreamState => ({
  ...state,
  organizations: getCopyableTargetOrgs(user),
});

export const copyws$ = persistentReducedStream('copyws$', startState, [
  streamReducer(currentUser$, currentUserReducer),
  streamReducer(workspacesAndPermissionContext$, workspacesReducer),
  reducer(copySuccess, copySuccessReducer),
  reducer(copyError, copyErrorReducer),
  reducer(resetCopyWs, resetReducer),
]);

export type CopyWsViewModel = CopyWsStreamState & WithI18nLocale;

export const copyWsViewModel$ = combineLatest([copyws$, i18nLocale$]).pipe(
  map(([state, i18nLocale]): CopyWsViewModel => ({
    ...state,
    i18nLocale,
  }))
);

export const copyWsRoutines = collectRoutines(handleCopyWorkspaces);
